import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  People,
  Description,
  GTranslate,
  School,
  Star,
  StarBorder,
  TrendingUp,
  Storage,
  Refresh
} from '@mui/icons-material';
import { supabase } from '../../services/supabaseClient';
import axios from 'axios';

const SystemHealth = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      const response = await axios.get('/api/admin/system-health', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setHealthData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 120000); // refresh every 2 min
    return () => clearInterval(interval);
  }, [fetchHealth]);

  if (loading && !healthData) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !healthData) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const d = healthData || {};

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">
          System Health
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchHealth} disabled={loading}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={3}>
        {/* Total Users */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <People color="primary" />
                <Typography variant="h6" ml={1}>Total Users</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {(d.totalUsers || 0).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="textSecondary" mt={0.5}>
                Active last hour: {d.activeLastHour || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Active last 24h: {d.activeLastDay || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Subscriptions */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Star color="secondary" />
                <Typography variant="h6" ml={1}>Subscriptions</Typography>
              </Box>
              <Box display="flex" gap={1} mb={1}>
                <Chip label={`${d.premiumUsers || 0} Premium`} color="secondary" size="small" icon={<Star />} />
                <Chip label={`${d.freeUsers || 0} Free`} size="small" variant="outlined" icon={<StarBorder />} />
              </Box>
              <Typography variant="body2" color="textSecondary">
                {d.totalUsers ? ((d.premiumUsers / d.totalUsers) * 100).toFixed(1) : 0}% conversion rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Usage Last 24h */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <TrendingUp color="info" />
                <Typography variant="h6" ml={1}>Last 24h</Typography>
              </Box>
              <Typography variant="h4" color="info.main">
                {(d.eventsLast24h || 0).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                translation requests
              </Typography>
              <Typography variant="body2" color="textSecondary" mt={0.5}>
                {(d.wordsLast24h || 0).toLocaleString()} words &bull; {(d.charsLast24h || 0).toLocaleString()} chars
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Database */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Storage color="warning" />
                <Typography variant="h6" ml={1}>Database</Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                {d.dbSizeFormatted || 'N/A'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total database size
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Content Stats */}
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Description color="primary" />
                <Typography variant="h6" ml={1}>Documents</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {(d.documentCount || 0).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                uploaded across all users
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <GTranslate color="secondary" />
                <Typography variant="h6" ml={1}>Saved Translations</Typography>
              </Box>
              <Typography variant="h4" color="secondary">
                {(d.translationCount || 0).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                saved by all users
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <School color="info" />
                <Typography variant="h6" ml={1}>Flashcards</Typography>
              </Box>
              <Typography variant="h4" color="info.main">
                {(d.flashcardCount || 0).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                created by all users
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {d.timestamp && (
        <Typography variant="caption" color="textSecondary" mt={2} display="block">
          Last updated: {new Date(d.timestamp).toLocaleTimeString()}
        </Typography>
      )}
    </Box>
  );
};

export default SystemHealth;
